import type {
  FilterConfig,
  TranscriptFilterConfig,
  TranscriptRole,
} from "@/types/metrics/metadata"

import {
  combineWithJoins,
  type CombineOps,
  type FilterJoin,
} from "../../filter-list"

export interface PreviewMessage {
  role: TranscriptRole
  index: number
}

const INDEX_SET_OPS: CombineOps<Set<number>> = {
  intersect: (left, right) =>
    new Set([...left].filter((index) => right.has(index))),
  union: (left, right) => new Set([...left, ...right]),
  empty: new Set(),
}

function applyFilter<T extends PreviewMessage>(
  messages: T[],
  filter: FilterConfig,
): T[] {
  if (filter.type === "range") {
    return filter.mode === "first_n"
      ? messages.slice(0, filter.count)
      : messages.slice(-filter.count)
  }
  return messages.filter((message) => filter.roles.includes(message.role))
}

function resolveFilterIndices<T extends PreviewMessage>(
  messages: T[],
  filter: FilterConfig,
): Set<number> {
  return new Set(applyFilter(messages, filter).map((message) => message.index))
}

function composeIndices<T extends PreviewMessage>(
  messages: T[],
  filters: FilterConfig[],
  joins: FilterJoin[],
): Set<number> {
  return combineWithJoins(
    filters,
    joins,
    (filter) => resolveFilterIndices(messages, filter),
    INDEX_SET_OPS,
  )
}

function applyComposedFilters<T extends PreviewMessage>(
  messages: T[],
  config: TranscriptFilterConfig,
): Set<number> {
  const includes = config.include ?? []
  const excludes = config.exclude ?? []
  const includeJoins = config.include_joins ?? []
  const excludeJoins = config.exclude_joins ?? []

  const included =
    includes.length === 0
      ? new Set(messages.map((message) => message.index))
      : composeIndices(messages, includes, includeJoins)
  if (included.size === 0) {
    return included
  }
  const excluded = composeIndices(messages, excludes, excludeJoins)
  return new Set([...included].filter((index) => !excluded.has(index)))
}

function applyLegacyFilters<T extends PreviewMessage>(
  messages: T[],
  config: TranscriptFilterConfig,
): Set<number> {
  const includeFilters = config.include ?? []
  const excludeFilters = config.exclude ?? []

  let included: T[] =
    includeFilters.length === 0
      ? [...messages]
      : applyFilter(messages, includeFilters[0])
  for (let i = 1; i < includeFilters.length; i++) {
    included = applyFilter(included, includeFilters[i])
  }

  for (const filter of excludeFilters) {
    const toExclude = applyFilter(included, filter)
    const excludeIndices = new Set(toExclude.map((message) => message.index))
    included = included.filter((message) => !excludeIndices.has(message.index))
  }

  return new Set(included.map((message) => message.index))
}

/**
 * Mirrors the backend gate: configs without *_joins arrays use the legacy
 * sequential-apply semantics so saved metrics preview the same way they
 * scored. Configs with joins use top-to-bottom left-associative composition.
 */
export function applyFilters<T extends PreviewMessage>(
  messages: T[],
  config: TranscriptFilterConfig,
): Set<number> {
  const hasJoins =
    (config.include_joins?.length ?? 0) > 0 ||
    (config.exclude_joins?.length ?? 0) > 0
  return hasJoins
    ? applyComposedFilters(messages, config)
    : applyLegacyFilters(messages, config)
}
